import React, { useCallback, useMemo, useState } from 'react'
import { useSessionState } from '@btime/react-session'

import DynamicForm from '../../../containers/dynamic/form/DynamicForm'
import AutofillFocusContext from '../../../containers/dynamic/form/replies/context/AutofillFocusContext'
import { getAutofillConfig } from '../../../containers/dynamic/form/_form/formatRepliesFormData'
import { useImperativeComponents } from '../../../containers/dynamic/form/replies/context/ImperativeComponentsContext'
import { useExecutionDynamicServiceOrders, useExecutionDynamicServiceOrdersActions } from './_form/ExecutionDynamicServiceOrdersHandler'
import { useSessionTokenForAutofill } from './_context/SessionTokenForAutofillContext'
import { useQuestions, useReplies, useDynamicServiceOrders } from './_load/DynamicServiceOrdersLoader'
import { useAutofillFieldValues } from './_requests/useAutofillFieldValues'
import { fetchAutofillValue } from '../../serviceOrders/executionV2/_requests/fetchAutofillValue'
import GeneralPanel from './general/GeneralPanel'

const ExecutionDynamicServiceOrder = () => {
  const dynamicServiceOrder = useDynamicServiceOrders()
  const executionForm = useExecutionDynamicServiceOrders()
  const { setForm } = useExecutionDynamicServiceOrdersActions()
  const { user } = useSessionState()
  const { components } = useImperativeComponents()
  const [autofillLoading, setAutofillLoading] = useState(false)

  const questions = useQuestions()
  const replies = useReplies()

  const place = executionForm?.place ?? dynamicServiceOrder?.place
  const currentUser = dynamicServiceOrder?.currentUser
  const placeId = place?.id
  const userId = currentUser?.id ?? user?.id
  const sessionTokenForAutofill = useSessionTokenForAutofill()

  const { placeFieldValues, userFieldValues: userFieldValuesFromHook } = useAutofillFieldValues(
    placeId,
    userId,
    sessionTokenForAutofill
  )

  const userFieldValuesResolved =
    currentUser != null
      ? [
          ...(currentUser?.contract?.fieldValues ?? []),
          ...(currentUser?.fieldValues ?? [])
        ]
      : userFieldValuesFromHook

  const userForAutofill = currentUser ?? user

  const serviceOrderLike = useMemo(
    () => (place || userForAutofill ? { place, user: userForAutofill } : null),
    [place, userForAutofill]
  )

  const autofillContext = useMemo(
    () =>
      serviceOrderLike
        ? { serviceOrder: serviceOrderLike, placeFieldValues, userFieldValues: userFieldValuesResolved }
        : undefined,
    [serviceOrderLike, placeFieldValues, userFieldValuesResolved]
  )

  const handleChange = useCallback(
    v => setForm({ ...executionForm, ...v }),
    [executionForm, setForm]
  )

  const setAutofillValue = useCallback(
    (questionId, value) => {
      if (value === undefined || value === null) return
      const comp = components?.current?.[questionId]
      if (comp?.setValue) comp.setValue(value)
    },
    [components]
  )

  const onApplyAutofillFocus = useCallback(
    async (questionId, question) => {
      if (!serviceOrderLike || !components?.current) return
      const config = getAutofillConfig(question)
      if (!config) return

      const comp = components.current[questionId]
      if (!comp?.setValue) return

      const { source, fieldId } = config
      setAutofillLoading(true)
      try {
        const value = await fetchAutofillValue(serviceOrderLike, source, fieldId, {
          sessionToken: sessionTokenForAutofill
        })
        if (value !== undefined && value !== null) comp.setValue(value)
      } finally {
        setAutofillLoading(false)
      }
    },
    [serviceOrderLike, components, sessionTokenForAutofill]
  )

  const autofillFocusValue = useMemo(
    () => (setAutofillValue ? { setAutofillValue, onApplyAutofillFocus } : null),
    [setAutofillValue, onApplyAutofillFocus]
  )

  return (
    <AutofillFocusContext.Provider value={autofillFocusValue}>
      <DynamicForm
        questions={questions}
        replies={replies}
        onChange={handleChange}
        autofillContext={autofillContext}
      >
        <GeneralPanel />
      </DynamicForm>
    </AutofillFocusContext.Provider>
  )
}

export default ExecutionDynamicServiceOrder
